import {
  AMENDMENT_KIND_LABELS,
  HANDOVER_KIND_LABELS,
  type HandoverBody,
  type HandoverDetail,
} from '@acasa/shared'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AmendDialog } from '@/features/handovers/amend-dialog'
import type { SectionProps } from '@/features/handovers/body-utils'
import { RemarksSection, RoomsSection } from '@/features/handovers/section-condition'
import { KeysSection, MetersSection } from '@/features/handovers/section-inventory'
import { ObjectSection } from '@/features/handovers/section-object'
import { SignaturesSection } from '@/features/handovers/section-signatures'
import { ChangeList, VersionsSection } from '@/features/handovers/section-versions'
import {
  pdfUrl,
  previewPdfUrl,
  useAutosave,
  useBodyChanges,
  useCompleteHandover,
  useDiscardAmendment,
  useHandover,
  useUpdateAmendment,
} from '@/features/handovers/use-handovers'

type Step = {
  key: string
  label: string
  render: (props: SectionProps) => React.ReactNode
}

const STEPS: Step[] = [
  { key: 'objekt', label: 'Objekt & Beteiligte', render: (props) => <ObjectSection {...props} /> },
  { key: 'zaehler', label: 'Zählerstände', render: (props) => <MetersSection {...props} /> },
  { key: 'schluessel', label: 'Schlüssel', render: (props) => <KeysSection {...props} /> },
  { key: 'raeume', label: 'Räume & Mängel', render: (props) => <RoomsSection {...props} /> },
  { key: 'bemerkungen', label: 'Bemerkungen', render: (props) => <RemarksSection {...props} /> },
  {
    key: 'unterschriften',
    label: 'Unterschriften',
    render: (props) => <SignaturesSection {...props} />,
  },
]

/**
 * Last, and only once a protocol has been completed at least once — a plain
 * draft has no history to show.
 */
const VERSIONS_STEP: Step = {
  key: 'fassungen',
  label: 'Fassungen',
  render: (props) => <VersionsSection protocol={props.protocol} />,
}

function stepsFor(detail: HandoverDetail | undefined): Step[] {
  return detail && detail.versions.length >= 1 ? [...STEPS, VERSIONS_STEP] : STEPS
}

/**
 * What the form was seeded from. The local draft is seeded once and then owns
 * the body — later refetches must not clobber edits made since. But some
 * transitions replace the body ON THE SERVER: starting a change, switching it
 * to a Wesentliche Änderung (signatures cleared) or back, discarding it,
 * finalising it. When this key moves, the server's body is the truth again.
 */
function seedKey(detail: HandoverDetail): string {
  return `${detail.status}:${detail.amendment?.kind ?? '-'}:${detail.version}`
}

/**
 * Things worth mentioning before a protocol is frozen.
 *
 * Deliberately warnings, never blockers: a meter that genuinely could not be
 * read, or a party who refused to sign, are real outcomes that the protocol has
 * to be able to record. Refusing to complete would just push people to write it
 * on paper instead.
 */
function readinessWarnings(body: HandoverBody): string[] {
  const warnings: string[] = []

  const unreadMeters = body.meters.filter((meter) => !meter.value.trim())
  if (unreadMeters.length > 0) {
    warnings.push(
      `${unreadMeters.length} Zähler ohne Stand: ${unreadMeters
        .map((meter) => meter.label || 'ohne Bezeichnung')
        .join(', ')}`,
    )
  }

  const unnamed = body.parties.filter(
    (party) => !party.name.trim() && (party.role === 'handover' || party.role === 'receiver'),
  )
  if (unnamed.length > 0) warnings.push(`${unnamed.length} Beteiligte(r) ohne Namen`)

  if (!body.handoverAt) warnings.push('Kein Übergabedatum eingetragen')

  const present = body.parties.filter((party) => party.present)
  const signed = body.signatures.filter((entry) => entry.photoId)
  if (signed.length < present.length) {
    warnings.push(
      `${present.length - signed.length} anwesende Person(en) ohne digitale Unterschrift — ` +
        'im PDF erscheint eine Unterschriftszeile',
    )
  }

  return warnings
}

export function HandoverEditorPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { data: detail, isPending, isError, error } = useHandover(id)

  const [stepKey, setStepKey] = useState(STEPS[0]?.key ?? 'objekt')
  const [draft, setDraft] = useState<HandoverBody | null>(null)
  const [confirming, setConfirming] = useState(false)
  // False until the autosave has been flushed after the dialog opened — the
  // change list is computed from what the SERVER has.
  const [flushed, setFlushed] = useState(false)
  // When the dialog was opened. A change list fetched before that — the
  // dialog was opened earlier and edits were made since — is not shown.
  const [openedAt, setOpenedAt] = useState(0)
  const [amendOpen, setAmendOpen] = useState(false)
  const [discardOpen, setDiscardOpen] = useState(false)

  const readOnly = detail?.status === 'completed'
  const amending = detail?.status === 'amending'
  const amendment = amending ? (detail?.amendment ?? null) : null
  const autosave = useAutosave(id, !readOnly)
  const complete = useCompleteHandover(id)
  const updateAmendment = useUpdateAmendment(id)
  const discard = useDiscardAmendment(id)
  const changes = useBodyChanges(id, confirming && amending && flushed)

  // Seed local state, and re-seed when the server replaced the body (see
  // `seedKey`). Refetches that leave the key alone — a photo upload
  // invalidates the query — keep the edits made since.
  const seededFor = useRef<string | null>(null)
  useEffect(() => {
    if (!detail) return
    const key = seedKey(detail)
    if (seededFor.current === key) return
    seededFor.current = key
    setDraft(detail.body)
  }, [detail])

  const body = readOnly ? (detail?.body ?? null) : draft

  const patch = useCallback(
    (update: (body: HandoverBody) => HandoverBody) => {
      setDraft((current) => {
        if (!current) return current
        const next = update(current)
        autosave.save(next)
        return next
      })
    },
    [autosave],
  )

  /**
   * A Kleine Korrektur becomes a Wesentliche Änderung. Flushed first: the
   * server clears the signatures on the body it HAS, and the form is then
   * re-seeded from that — an edit still waiting in the debounce would be lost.
   */
  const switchToMajor = useCallback(async () => {
    await autosave.flush()
    await updateAmendment.mutateAsync({ kind: 'major' })
  }, [autosave, updateAmendment])

  const openCompletion = async () => {
    complete.reset()
    updateAmendment.reset()
    setFlushed(false)
    setOpenedAt(Date.now())
    setConfirming(true)
    // Before anything is shown: the PDF and the change list are both rendered
    // from the server's copy, and the last keystrokes may still be in the
    // debounce.
    await autosave.flush()
    setFlushed(true)
  }

  const steps = stepsFor(detail)
  const warnings = useMemo(() => (body ? readinessWarnings(body) : []), [body])
  const step = steps.find((entry) => entry.key === stepKey) ?? steps[0]
  const stepIndex = steps.findIndex((entry) => entry.key === step?.key)

  if (isPending) {
    return <p className="text-muted p-8 text-center text-sm">Wird geladen …</p>
  }

  if (isError || !detail || !body || !step) {
    return (
      <div className="p-8">
        <p className="border-danger/30 bg-danger/5 text-danger border px-3 py-2 text-xs">
          {error instanceof Error ? error.message : 'Protokoll konnte nicht geladen werden'}
        </p>
        <Link to="/protokolle" className="text-brand mt-4 inline-block text-sm underline">
          Zurück zur Übersicht
        </Link>
      </div>
    )
  }

  const sectionProps: SectionProps = { protocol: detail, body, patch, readOnly, switchToMajor }
  const nextVersion = detail.version + 1
  const changesFailed = changes.isError && changes.errorUpdatedAt >= openedAt
  const changesLoading =
    !changesFailed && (!flushed || changes.isFetching || changes.dataUpdatedAt < openedAt)
  const changeList = changesLoading ? [] : (changes.data?.changes ?? [])
  const overreaches = amendment?.kind === 'minor' && changeList.some((change) => change.substantive)

  const saveLabel = {
    saved: autosave.lastSavedAt
      ? `gespeichert ${autosave.lastSavedAt.toLocaleTimeString('de-DE', {
          hour: '2-digit',
          minute: '2-digit',
        })}`
      : 'gespeichert',
    saving: 'speichert …',
    dirty: 'ungespeicherte Änderungen',
    error: 'Speichern fehlgeschlagen',
  }[autosave.state]

  return (
    <div className="flex h-full flex-col">
      <header className="border-line bg-raised shrink-0 border-b px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to="/protokolle"
              className="text-faint hover:text-ink text-[11px] underline underline-offset-2"
            >
              ← Übergabeprotokolle
            </Link>
            <h1 className="font-display text-ink mt-1 text-lg leading-tight">
              {HANDOVER_KIND_LABELS[detail.kind]}
            </h1>
            <p className="text-muted mt-0.5 text-xs">
              {detail.estate.externalNumber ?? 'ohne Objekt-Nr.'}
              {detail.estate.street &&
                ` · ${detail.estate.street} ${detail.estate.houseNumber ?? ''}`}
              {detail.estate.city && `, ${detail.estate.city}`}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {readOnly && (
              <span className="border-done/40 bg-done/10 text-done border px-2 py-1 text-[11px]">
                abgeschlossen{detail.version >= 2 && ` · Fassung ${detail.version}`}
              </span>
            )}
            {amending && (
              <span className="border-warning/40 bg-warning/10 text-warning border px-2 py-1 text-[11px]">
                in Änderung · Fassung {nextVersion}
              </span>
            )}
            {!readOnly && (
              <span
                className={`text-[11px] ${
                  autosave.state === 'error' ? 'text-danger' : 'text-faint'
                }`}
              >
                {saveLabel}
              </span>
            )}

            {readOnly ? (
              <>
                <a
                  href={pdfUrl(detail.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="border-line text-muted hover:text-ink border px-3 py-2 text-xs transition-colors"
                >
                  PDF herunterladen
                </a>
                <button
                  type="button"
                  onClick={() => setAmendOpen(true)}
                  className="bg-brand text-surface hover:bg-brand-strong px-3 py-2 text-xs font-medium transition-colors"
                >
                  Änderung vornehmen
                </button>
              </>
            ) : (
              <>
                <a
                  href={previewPdfUrl(detail.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="border-line text-muted hover:text-ink border px-3 py-2 text-xs transition-colors"
                >
                  PDF-Vorschau
                </a>
                {amending && (
                  <button
                    type="button"
                    onClick={() => {
                      discard.reset()
                      setDiscardOpen(true)
                    }}
                    className="text-faint hover:text-danger px-1 py-2 text-xs underline underline-offset-2 transition-colors"
                  >
                    Änderung verwerfen
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void openCompletion()}
                  className="bg-brand text-surface hover:bg-brand-strong px-3 py-2 text-xs font-medium transition-colors"
                >
                  {amending ? 'Änderung abschließen' : 'Protokoll abschließen'}
                </button>
              </>
            )}
          </div>
        </div>

        {amendment && (
          <p className="text-muted mt-2 truncate text-xs" title={amendment.reason}>
            {AMENDMENT_KIND_LABELS[amendment.kind]} · {amendment.reason}
          </p>
        )}
      </header>

      {autosave.state === 'error' && (
        <p className="border-danger/30 bg-danger/5 text-danger mx-4 mt-3 border px-3 py-2 text-xs">
          Änderungen konnten nicht gespeichert werden. Die Eingaben stehen noch im Formular —
          Verbindung prüfen, dann weitertippen; es wird automatisch erneut versucht.
        </p>
      )}

      <div className="grid min-h-0 flex-1 lg:grid-cols-[15rem_1fr]">
        {/* Vertical rail on a desktop, horizontal stepper on a phone. */}
        <nav className="border-line bg-surface min-w-0 shrink-0 border-b lg:border-r lg:border-b-0">
          <ol className="flex overflow-x-auto lg:flex-col lg:overflow-visible lg:py-2">
            {steps.map((entry, index) => {
              const active = entry.key === step.key
              return (
                <li key={entry.key} className="shrink-0 lg:shrink">
                  <button
                    type="button"
                    onClick={() => setStepKey(entry.key)}
                    className={`flex w-full items-center gap-2 border-b-2 px-4 py-3 text-left text-xs whitespace-nowrap transition-colors lg:border-b-0 lg:border-l-2 ${
                      active
                        ? 'border-brand bg-brand-soft text-brand font-medium'
                        : 'text-muted hover:text-ink border-transparent'
                    }`}
                  >
                    <span className="tnum text-faint font-mono text-[10px]">{index + 1}</span>
                    {entry.label}
                  </button>
                </li>
              )
            })}
          </ol>
        </nav>

        <div className="min-h-0 min-w-0 overflow-auto">
          <div className="max-w-3xl px-4 py-5">
            <h2 className="font-display text-ink mb-4 text-xl">{step.label}</h2>

            {step.render(sectionProps)}

            <div className="border-line mt-8 flex items-center justify-between gap-3 border-t pt-4">
              <button
                type="button"
                disabled={stepIndex <= 0}
                onClick={() => setStepKey(steps[stepIndex - 1]?.key ?? step.key)}
                className="border-line text-muted hover:text-ink border px-4 py-2.5 text-xs transition-colors disabled:opacity-30"
              >
                Zurück
              </button>
              <span className="text-faint tnum font-mono text-[11px]">
                {stepIndex + 1}/{steps.length}
              </span>
              <button
                type="button"
                disabled={stepIndex >= steps.length - 1}
                onClick={() => setStepKey(steps[stepIndex + 1]?.key ?? step.key)}
                className="bg-ink text-surface hover:bg-brand px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-30"
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      </div>

      {confirming && (
        <div className="bg-ink/40 fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6">
          <div className="bg-surface border-line max-h-full w-full max-w-lg overflow-auto border p-5">
            <h2 className="font-display text-ink text-lg">
              {amending ? `Fassung ${nextVersion} abschließen` : 'Protokoll abschließen'}
            </h2>
            {amending ? (
              <p className="text-muted mt-2 text-xs leading-relaxed">
                Die Änderung wird als Fassung {nextVersion} festgeschrieben und als neues PDF
                erzeugt. Die bisherigen Fassungen bleiben erhalten; die Änderungshistorie wird im
                PDF ausgewiesen.
              </p>
            ) : (
              <p className="text-muted mt-2 text-xs leading-relaxed">
                Das Protokoll wird als PDF erzeugt und danach <strong>nicht mehr änderbar</strong>.
                Es wird nicht automatisch nach onOffice übertragen — das PDF anschließend
                herunterladen und dort beim Objekt ablegen.
              </p>
            )}

            {autosave.state === 'error' && (
              <p className="border-danger/30 bg-danger/5 text-danger mt-4 border px-3 py-2 text-xs">
                Die letzten Eingaben konnten nicht gespeichert werden und wären nicht enthalten.
                Verbindung prüfen und erneut versuchen.
              </p>
            )}

            {amending && (
              <div className="mt-4">
                <p className="text-faint mb-2 text-[10px] tracking-[0.14em] uppercase">
                  Was sich ändert
                </p>
                {changesLoading ? (
                  <p className="text-faint text-xs">wird ermittelt …</p>
                ) : changesFailed ? (
                  <p className="text-danger text-xs">
                    {changes.error instanceof Error
                      ? changes.error.message
                      : 'Änderungen konnten nicht ermittelt werden'}
                  </p>
                ) : changeList.length === 0 ? (
                  <p className="text-muted text-xs">
                    Keine Änderungen gegenüber Fassung {detail.version}. Es wird trotzdem eine neue
                    Fassung erzeugt.
                  </p>
                ) : (
                  <>
                    <ChangeList changes={changeList} />
                    {changeList.some((change) => change.substantive) && (
                      <p className="text-faint mt-1.5 flex items-center gap-1.5 text-[11px]">
                        <span className="bg-warning size-1.5 rounded-full" aria-hidden />
                        unterschriebene Angabe
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {overreaches && (
              <div className="border-warning/40 bg-warning/10 mt-4 border p-3">
                <p className="text-ink text-xs leading-relaxed">
                  Diese Korrektur ändert Angaben, die die Beteiligten mit ihrer Unterschrift
                  bestätigt haben (markierte Zeilen). Als wesentliche Änderung fortsetzen? Dann
                  unterschreiben alle Beteiligten erneut.
                </p>
                <button
                  type="button"
                  disabled={updateAmendment.isPending}
                  onClick={async () => {
                    try {
                      await switchToMajor()
                    } catch {
                      return // shown below from the mutation's own error
                    }
                    setConfirming(false)
                    // The signatures were just cleared: that is where the
                    // work is now.
                    setStepKey('unterschriften')
                  }}
                  className="border-brand text-brand hover:bg-brand-soft mt-3 border px-3 py-2 text-xs font-medium transition-colors disabled:opacity-40"
                >
                  {updateAmendment.isPending
                    ? 'wird umgestellt …'
                    : 'Zu wesentlicher Änderung wechseln'}
                </button>
                {updateAmendment.isError && (
                  <p className="text-danger mt-2 text-xs">
                    {updateAmendment.error instanceof Error
                      ? updateAmendment.error.message
                      : 'Umstellen fehlgeschlagen'}
                  </p>
                )}
              </div>
            )}

            {warnings.length > 0 && (
              <div className="border-warning/40 bg-warning/10 mt-4 border p-3">
                <p className="text-ink text-xs font-medium">Vor dem Abschließen prüfen:</p>
                <ul className="text-muted mt-1.5 list-inside list-disc space-y-1 text-[11px]">
                  {warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {complete.isError && (
              <p className="text-danger mt-3 text-xs">
                {complete.error instanceof Error
                  ? complete.error.message
                  : 'Abschließen fehlgeschlagen'}
              </p>
            )}

            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="border-line text-muted hover:text-ink border px-4 py-2.5 text-xs transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="button"
                disabled={complete.isPending || !flushed || (amending && changesLoading)}
                onClick={async () => {
                  // Flushed again: cheap, and nothing may slip between the
                  // dialog opening and the PDF being rendered.
                  await autosave.flush()
                  try {
                    await complete.mutateAsync()
                  } catch {
                    return // shown above from the mutation's own error
                  }
                  setConfirming(false)
                  navigate(`/protokolle/${detail.id}`)
                }}
                className="bg-brand text-surface hover:bg-brand-strong px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
              >
                {complete.isPending
                  ? 'wird erzeugt …'
                  : !amending
                    ? 'Abschließen und PDF erzeugen'
                    : overreaches
                      ? 'Trotzdem als Korrektur abschließen'
                      : `Fassung ${nextVersion} erzeugen`}
              </button>
            </div>
          </div>
        </div>
      )}

      {amendOpen && (
        <AmendDialog
          protocol={detail}
          onClose={() => setAmendOpen(false)}
          onStarted={() => setAmendOpen(false)}
        />
      )}

      {discardOpen && (
        <div className="bg-ink/40 fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6">
          <div className="bg-surface border-line max-h-full w-full max-w-md overflow-auto border p-5">
            <h2 className="font-display text-ink text-lg">Änderung verwerfen</h2>
            <p className="text-muted mt-2 text-xs leading-relaxed">
              Alle Änderungen seit Beginn werden verworfen, auch neu aufgenommene Fotos. Fassung{' '}
              {detail.version} bleibt gültig.
            </p>

            {discard.isError && (
              <p className="text-danger mt-3 text-xs">
                {discard.error instanceof Error
                  ? discard.error.message
                  : 'Verwerfen fehlgeschlagen'}
              </p>
            )}

            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setDiscardOpen(false)}
                className="border-line text-muted hover:text-ink border px-4 py-2.5 text-xs transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="button"
                disabled={discard.isPending}
                onClick={async () => {
                  // An edit still in the debounce would otherwise be PATCHed
                  // onto a protocol that is completed again (409).
                  autosave.cancel()
                  try {
                    await discard.mutateAsync(undefined)
                  } catch {
                    return
                  }
                  setDiscardOpen(false)
                }}
                className="border-danger text-danger hover:bg-danger/5 border px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
              >
                {discard.isPending ? 'wird verworfen …' : 'Änderung verwerfen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
